import { v4 } from 'uuid';
import type {
  EventPayload,
  BatchQueue,
  Reporter,
} from '@wix/panorama-common-shared/types';
import {
  type DurationTracker,
  createDurationTracker,
} from '../duration-tracker';

export interface GlobalConfig<
  Payload extends unknown = EventPayload,
  State extends Record<string, any> = {},
> {
  getSessionId(): string;

  setSessionId(sessionId: string): void;

  getSessionTime(): number;

  getReporter(): Reporter<Payload> | null;

  setReporter(reporter: Reporter<Payload>): void;

  initBatchQueue(batchQueue: BatchQueue): void;

  getBatchQueue(): BatchQueue;

  getStateValue<K extends keyof State>(key: K): State[K];

  setStateValue<K extends keyof State>(key: K, value: State[K]): void;

  getDurationTracker(): DurationTracker;

  clearState(): void;
}

class GlobalConfigImpl<
  Payload extends unknown = EventPayload,
  State extends Record<string, any> = {},
> implements GlobalConfig<Payload, State>
{
  private sessionId: string = '';
  private reporter: Reporter<Payload> | null = null;
  private batchQueue: BatchQueue | null = null;
  private durationTracker = createDurationTracker();
  private readonly state = new Map();
  private readonly sessionStart = Date.now();

  getSessionId() {
    this.sessionId = this.sessionId || v4();

    return this.sessionId;
  }

  setSessionId(sessionId: string) {
    this.sessionId = sessionId;
  }

  getSessionTime() {
    return Date.now() - this.sessionStart;
  }

  getReporter() {
    return this.reporter;
  }

  setReporter(reporter: Reporter<Payload>) {
    this.reporter = reporter;
  }

  initBatchQueue(batchQueue: BatchQueue) {
    this.batchQueue = this.batchQueue ?? batchQueue;
  }

  getBatchQueue(): BatchQueue {
    return this.batchQueue!;
  }

  getDurationTracker() {
    return this.durationTracker;
  }

  getStateValue<K extends keyof State>(key: K): State[K] {
    return this.state.get(key);
  }

  setStateValue<K extends keyof State>(key: K, value: State[K]) {
    this.state.set(key, value);
  }

  clearState() {
    this.state.clear();
    this.durationTracker = createDurationTracker();
  }
}

export const createGlobalConfig = <
  Payload extends unknown = EventPayload,
  State extends Record<string, any> = {},
>(): GlobalConfig<Payload, State> => new GlobalConfigImpl<Payload, State>();
